/**
 * This custom dialog is a simple dialog component that can be used to display a message and a close button.
 * The difference with the Dialog and AlertDialog components, is that this one does not creates the overlay that blocks the whole screen.
 */
import type { ReactNode } from 'react';
import { Button } from './button.js';

interface CustomDialogProps {
  title?: string;
  text?: string;
  children?: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  closeButtonText?: string;
  onConfirm?: () => void;
  confirmButtonText?: string;
  loading?: boolean;
}

export function CustomDialog({
  title,
  text,
  children,
  open,
  setOpen,
  closeButtonText,
  onConfirm,
  confirmButtonText,
  loading = false,
}: CustomDialogProps) {
  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow p-6 max-w-md w-full">
        {title && <h2 className="text-lg font-bold">{title}</h2>}
        {text && <p className="mt-4 text-md text-gray-600 text-wrap">{text}</p>}
        {children && <div className="mt-2">{children}</div>}
        <div className="mt-4 flex gap-x-2 justify-end">
          <Button variant="dismiss" onClick={() => setOpen(false)} disabled={loading}>
            {closeButtonText || 'Cancel'}
          </Button>
          {onConfirm && (
            <Button variant="destructive" onClick={onConfirm} loading={loading}>
              {confirmButtonText || 'Confirm'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
